import styled from 'styled-components';
import theme from 'styled-theming';

const cardBorder = theme('name', {
  light: 'rgba(28, 28, 28, 0.12)',
  dark: 'rgba(255, 255, 255, 0.15)',
});

const filtersBackground = theme('name', {
  light: 'rgb(242, 242, 242)',
  dark: 'rgb(28, 28, 28)',
});

const cardBackground = theme('name', {
  light: '#FFFFFF',
  dark: '#242424',
});

const mutedText = theme('name', {
  light: '#6B6B6B',
  dark: '#A6A6A6',
});

const positiveColor = '#008557';
const negativeColor = '#DD1D21';
const investigateBg = '#FBCE07';
const investigateText = '#1C1C1C';

export const HealthCheckContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 16px;
  overflow-y: auto;

  .filters-bar {
    display: grid;
    grid-template-columns: repeat(2, 300px);
    gap: 16px;
    padding: 12px 16px;
    background: ${filtersBackground};
    border-radius: 6px;
  }

  .filter-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .filter-label {
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.theme.text.default};
  }

  .summary-bar {
    display: flex;
    gap: 12px;
    padding: 0 16px;
  }

  .summary-card {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
    width: 220px;
    padding: 10px 16px;
    background: ${cardBackground};
    border: 1px solid ${cardBorder};
    border-radius: 6px;
    color: ${(props) => props.theme.text.default};
  }

  .summary-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  .summary-icon-exposure {
    color: #0097a9;
  }

  .summary-icon-awaiting {
    color: #fbce07;
  }

  .summary-icon-matched {
    color: ${positiveColor};
  }

  .summary-icon-mismatch {
    color: ${negativeColor};
  }

  .summary-text {
    display: flex;
    flex-direction: column;
  }

  .summary-value {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
  }

  .summary-unit {
    margin-left: 4px;
    font-size: 12px;
    font-weight: 400;
  }

  .summary-label {
    font-size: 12px;
    color: ${mutedText};
  }

  .country-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, 282px);
    gap: 16px;
    padding: 0 16px 12px;
  }

  .country-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 282px;
    height: 294px;
    gap: 12px;
    padding: 12px;
    background: ${cardBackground};
    border: 1px solid ${cardBorder};
    border-radius: 12px;
    box-shadow:
      0 2px 5px rgba(0, 0, 0, 0.1),
      0 0 2px rgba(0, 0, 0, 0.1);
  }

  .country-card[draggable='true'] {
    cursor: grab;
  }

  .country-card.locked {
    border: 2px solid #0097a9;
    cursor: default;
  }

  .country-card.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .country-card.drag-over {
    border: 2px dashed #0097a9;
  }

  .country-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 24px;
  }

  .country-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 20px;
    line-height: 24px;
    font-weight: 600;
    color: ${(props) => props.theme.text.default};
  }

  .country-name span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .country-name svg {
    border-radius: 2px;
    flex-shrink: 0;
    height: 26px;
    width: 26px;
  }

  .favorite-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 2px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${mutedText};

    svg {
      width: 20px;
      height: 20px;
    }

    &.is-favorite {
      color: ${investigateBg};
    }
  }

  .country-card-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .net-label {
    font-family: Arial, sans-serif;
    font-size: 18px;
    line-height: 24px;
    color: ${mutedText};
  }

  .net-value {
    display: grid;
    grid-template-columns: minmax(0, auto) auto;
    align-items: end;
    justify-content: space-between;
    gap: 8px;
    font-weight: 700;
    color: ${(props) => props.theme.text.default};
  }

  .net-value-up {
    svg {
      color: ${positiveColor};
    }
  }

  .net-value-down {
    svg {
      color: ${negativeColor};
    }
  }

  .net-amount {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 32px;
    line-height: 40px;
    letter-spacing: 0;
  }

  .net-unit {
    padding-bottom: 4px;
    font-family: Arial, sans-serif;
    font-size: 16px;
    line-height: 20px;
    color: ${mutedText};
    font-weight: 400;
  }

  .country-card-stats {
    display: flex;
    gap: 32px;
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .stat-count-row {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 28px;
  }

  .stat-count {
    font-size: 24px;
    line-height: 28px;
    font-weight: 600;
    color: ${(props) => props.theme.text.default};
  }

  .stat-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: Arial, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${mutedText};
  }

  .stat-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .stat-icon-mismatch {
    color: ${negativeColor};
  }

  .stat-icon-pending {
    color: ${investigateBg};
  }

  .stat-icon-matched {
    color: ${positiveColor};
  }

  .investigate-btn {
    width: calc(100% - 8px);
    min-height: 56px;
    padding: 16px;
    margin: 0 4px;
    border: none;
    border-radius: 8px;
    background: ${investigateBg};
    color: ${investigateText};
    font-size: 20px;
    line-height: 24px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      filter: brightness(0.95);
    }
  }
`;
